import React, { useState } from "react";
import { Modal, Fade, Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { API_ENDPOINT } from "../../services/api/resources";
import DButton from "../Dashboard/DashboardButtons/Dbutton";
import DButtonOutlined from "../Dashboard/DashboardButtons/dButtonOutlined";
import DPasswordInput from "../Dashboard/DashboardInputs/DPasswordInput";
import HeaderTitle from "../common/HeaderTitle/HeaderTitle";
import PopupFrame from "../Popups/PopupFrame/PopupFrame";
import { emptyCheck, validatePassword, validateConfirmPassword} from "../../utils/validations"
import styles from "../common/ProfileCard/ProfileCard.module.css"

const ChangePasswordModal = ({ open, onClose, buyerBtn, vendorBtn }) => {
  const dispatch = useDispatch();
  const loginData = useSelector((state) => state.loginData.value);
  const [auth_token] = useState(loginData?.auth_token);

  const [oldPassword, setOldPassword] = useState({ value: "", isValid: true });
  const [newPassword, setNewPassword] = useState({ value: "", isValid: true });
  const [confirmNewPassword, setConfirmNewPassword] = useState({ value: "", isValid: true });
  const [errorState, setErrorState] = useState({ error: false, errorMsg: "" });
  const [loadingState, setLoadingState] = useState(false);
  const [success, setSuccess] = useState(false);

  const resetForm = () => {
    setOldPassword({ value: "", isValid: true });
    setNewPassword({ value: "", isValid: true });
    setConfirmNewPassword({ value: "", isValid: true });
    setErrorState({ error: false, errorMsg: "" });
  };

  const validateForm = () => {
    const isOldPasswordValid = emptyCheck(oldPassword.value);
    const isPasswordValid = validatePassword(newPassword.value);
    const isConfPasswordValid = 
      validateConfirmPassword(newPassword.value, confirmNewPassword.value) && 
      emptyCheck(confirmNewPassword.value);

    setOldPassword(prev => ({ ...prev, isValid: isOldPasswordValid }));
    setNewPassword(prev => ({ ...prev, isValid: isPasswordValid }));
    setConfirmNewPassword(prev => ({ ...prev, isValid: isConfPasswordValid }));

    return isOldPasswordValid && isPasswordValid && isConfPasswordValid;
  };

  const handleSubmit = async () => {
    const isValidated = validateForm();
    setLoadingState(true);
    
    if (isValidated) {
      try {
        await axios({
          method: "POST",
          url: `${API_ENDPOINT.change_password}`,
          data: {
            email: vendorBtn ? loginData?.user?.company_official_email : loginData?.user?.email,
            old_password: oldPassword.value,
            new_password: newPassword.value,
            confirm_password: confirmNewPassword.value,
          },
          headers: { Authorization: `Bearer ${auth_token}` },
        });
        setSuccess(true);
        resetForm();
      } catch (err) {
        setErrorState({
          error: true,
          errorMsg: Object.values(err?.response?.data),
        });
      } finally {
        setLoadingState(false);
      }
    } else {
      setLoadingState(false);
    }
  };

  const SettingsInputsHeading = (
    <HeaderTitle titleText={"Settings"} customclass={styles.popupHeading} />
  );

  const SettingsInputs = [
    <DPasswordInput
      label={"Old Password"}
      type={"password"}
      value={oldPassword.value}
      onChange={(e) => setOldPassword({
        value: e.target.value,
        isValid: validatePassword(e.target.value)
      })}
      error={!oldPassword.isValid}
      errorMsg={!emptyCheck(oldPassword.value) ? "Old Password is required" : "Invalid password"}
      size={"100%"}
    />,
    <DPasswordInput
      label={"New Password"}
      type={"password"}
      value={newPassword.value}
      onChange={(e) => setNewPassword({
        value: e.target.value,
        isValid: validatePassword(e.target.value)
      })}
      error={!newPassword.isValid}
      errorMsg={!emptyCheck(newPassword.value) ? "New Password is required" : "Invalid password"}
      customWrapperClass={styles.pwds}
      size={"44%"}
      info={true}
    />,
    <DPasswordInput
      label={"Confirm Password"}
      type={"password"}
      value={confirmNewPassword.value}
      onChange={(e) => setConfirmNewPassword({
        value: e.target.value,
        isValid: emptyCheck(e.target.value)
      })}
      error={!confirmNewPassword.isValid}
      errorMsg={
        !emptyCheck(confirmNewPassword.value) 
          ? "Confirm Password is required" 
          : "Password doesn't match"
      }
      customWrapperClass={styles.pwds}
      confirmPassword={true}
      comparePassword={newPassword.value}
      size={"44%"}
    />
  ];

  const SettingsInputsActionBtns = [
    <DButtonOutlined
      label={"Cancel"}
      onClick={onClose}
      btnWidth={"17rem"}
      btnHeight={"4rem"}
      buyerBtn={buyerBtn}
      vendorBtn={vendorBtn}
    />,
    <DButton
      label={"Save"}
      error={errorState.error}
      errorMsg={errorState.errorMsg}
      loadingState={loadingState}
      buyerBtn={buyerBtn}
      vendorBtn={vendorBtn}
      btnWidth={"17rem"}
      btnHeight={"4rem"}
      onClick={handleSubmit}
    />
  ];

  return (
    <>
      <Modal open={open} onClose={onClose} closeAfterTransition>
        <Fade in={open}>
          <div className={styles.flex} style={{ height: "100vh" }}>
            <PopupFrame
              customClass={styles.settingsPopup}
              inputChildren={SettingsInputs}
              poupHeading={SettingsInputsHeading}
              actionButtons={SettingsInputsActionBtns}
              closePopup={onClose}
            />
          </div>
        </Fade>
      </Modal>

      {success && (
        <PopupFrame
          message="Password changed successfully!"
          onClose={() => {
            setSuccess(false);
            onClose();
          }}
          buyerBtn={buyerBtn}
          vendorBtn={vendorBtn}
        />
      )}
    </>
  );
};

export default ChangePasswordModal;